import { type PayloadAction, createSlice } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import {
  afterFailedFetch,
  beforeFetch
} from "../../../redux/shared/paged-list.ts"
import { SCROLL_PAGE_SIZE } from "../../../redux/shared/paged-list.ts"
import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import type { RehydrateAction } from "../../../store.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import {
  updateListProject,
  updateProjectStatus
} from "../../Project/redux/project.thunks.ts"
import { projectsSliceName } from "./projects.constants.ts"
import {
  ProjectFetchType,
  fetchProjects,
  fetchProjectsFilterOptions
} from "./projects.thunks.ts"
import { type ProjectFilters, type ProjectsState } from "./projects.types.ts"

// Initial State
const initialState: ProjectsState = {
  // list
  listData: {
    results: [],
    count: 0,
    next: null,
    previous: null,
    pending_closed_win_count: 0
  },
  listRequestId: null,
  listDataLoaded: false,
  listQuickViewCounts: null,
  listFilters: {
    page: 1,
    page_size: SCROLL_PAGE_SIZE,
    q: null,
    project_status: null,
    project_manager: null,
    customer_name: null,
    end_date_after: null,
    end_date_before: null,
    created_window: null,
    created_after: null,
    created_before: null,
    ordering: null
  },

  // grid
  gridData: {
    results: [],
    count: 0,
    next: null,
    previous: null,
    pending_closed_win_count: 0
  },
  gridRequestId: null,
  gridFilters: {
    page: 1,
    page_size: 10,
    q: null,
    project_manager: null,
    project_status: null,
    customer_name: null,
    end_date_after: null,
    end_date_before: null,
    created_window: null,
    created_after: null,
    created_before: null,
    ordering: null
  },

  // common
  fetching: false,
  fetchingFilterOptions: false,
  filterOptions: {
    project_status: []
  },

  saveFetching: false,
  saveResult: null
}

const projectsSlice = createSlice({
  name: projectsSliceName,
  initialState,
  reducers: {
    projectsSetProp: generateSetPropReducer<ProjectsState>(),

    projectsSetListFilters: (
      state,
      action: PayloadAction<Partial<Partial<ProjectFilters>>>
    ) => {
      state.listFilters = { ...state.listFilters, ...action?.payload }
    },

    projectsSetGridFilters: (
      state,
      action: PayloadAction<Partial<Partial<ProjectFilters>>>
    ) => {
      state.gridFilters = { ...state.gridFilters, ...action?.payload }
    }
  },
  extraReducers: (builder) => {
    // Only the persisted filters come back; everything else restarts from
    // initialState so a filters-only payload cannot hollow out the slice.
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      const stored = action?.payload?.[projectsSliceName]
      if (!stored) return state

      return {
        ...initialState,
        listFilters: { ...initialState.listFilters, ...stored.listFilters },
        gridFilters: { ...initialState.gridFilters, ...stored.gridFilters }
      }
    })

    // fetchProjects
    builder
      .addCase(fetchProjects.pending, (state, action) => {
        state.fetching = true

        const isList = action.meta?.arg?.type === ProjectFetchType.LIST
        const key = isList ? "listData" : "gridData"
        state[isList ? "listRequestId" : "gridRequestId"] =
          action.meta.requestId
        state[key] = beforeFetch(
          state[key],
          initialState[key],
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        const { type, isMore, data } = action.payload
        const isList = type === ProjectFetchType.LIST
        const idKey = isList ? "listRequestId" : "gridRequestId"
        if (state[idKey] !== action.meta.requestId) return

        const key = isList ? "listData" : "gridData"
        state[idKey] = null

        if (isMore) {
          state[key] = {
            ...data,
            results: [
              ...(state?.[key]?.results || []),
              ...(data?.results || [])
            ]
          }
        } else {
          state[key] = data
        }

        if (isList && data?.quick_view_counts) {
          state.listQuickViewCounts = data.quick_view_counts
        }
        if (isList) state.listDataLoaded = true

        state.fetching = false
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        const isList = action.meta?.arg?.type === ProjectFetchType.LIST
        const idKey = isList ? "listRequestId" : "gridRequestId"
        if (state[idKey] !== action.meta.requestId) return

        state.fetching = false
        state[idKey] = null

        const key = isList ? "listData" : "gridData"
        state[key] = afterFailedFetch(
          state[key],
          initialState[key],
          action.meta?.arg?.isMore
        )
      })

    // fetchProjectsFilterOptions
    builder
      .addCase(fetchProjectsFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(fetchProjectsFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(fetchProjectsFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

    // updateProjectStatus
    builder.addCase(updateProjectStatus.fulfilled, (state, action) => {
      const { id, newStatus, mode } = action.payload
      const key: keyof ProjectsState = mode === "list" ? "listData" : "gridData"

      state[key].results = state?.[key]?.results?.map((p) =>
        p?.id === id ? { ...p, project_status: newStatus } : p
      )
    })

    // updateListProject
    builder.addCase(updateListProject.fulfilled, (state, action) => {
      const updatedProject = action?.payload

      state.listData.results = state.listData?.results?.map((p) =>
        p?.id === updatedProject?.id ? { ...p, ...updatedProject } : p
      )
    })

    builder.addCase(logout.fulfilled, () => {
      console.log("projects logout")
      return initialState
    })
  }
})

export const {
  projectsSetProp,
  projectsSetGridFilters,
  projectsSetListFilters
} = projectsSlice.actions

export default projectsSlice
